// components/PeriodForm/PeriodFormUI.jsx
import { F, INTL_ACTION, INTL_ENTITY } from '@/utils/intl';
import React from 'react';
import { Button, Popconfirm } from 'antd';
import VirtualizedList from 'rc-virtual-list';
import { CloseCircleOutlined, PlusOutlined } from '@ant-design/icons';
import SameDayTimeRangePicker from '@/components/MyDateTimePicker/SameDayRagePicker';
import MyDateTimeRangePicker from '@/components/MyDateTimePicker';
import DateFormat from '@/components/format/DateFormat';
import { useIntl } from 'umi';
import styles from './index.less';

const ITEM_HEIGHT = 50;

export const PeriodFormUI = ({
                               mode,
                               periods,
                               onAdd,
                               onRemove,
                               onChange,
                             }) => {
  const intl = useIntl();
  const [range, setRange] = React.useState([]);
  const [sameDayRange, setSameDayRange] = React.useState({});
  const [openIndex, setOpenIndex] = React.useState<number | null>(null);
  const rootRef = React.useRef<HTMLDivElement>(null);

  const getPeriodDate = (time: string, date?: string) => {
    if (!time) return date;
    if (typeof time === 'string' && time.length <= 8 && time.includes(':')) {
      const datePart = date ? date.split('T')[0] : '';
      return datePart ? `${datePart}T${time}` : time;
    }
    return time;
  };

  const transformSameDayRange = () => {
    const { date, start, end } = sameDayRange || {};
    return [
      date.clone().hour(parseInt(start.split(":")[0])).minute(parseInt(start.split(":")[1])).format(),
      date.clone().hour(parseInt(end.split(":")[0])).minute(parseInt(end.split(":")[1])).format(),
    ];
  };

  const handleAdd = () => {
    const rangeISODates = mode === 'block'
      ? range.map((date) => date.format())
      : transformSameDayRange();

    const period = {
      start: rangeISODates[0],
      end: rangeISODates[1],
    };

    const isDuplicate = periods?.some(p => p.start === period.start && p.end === period.end);
    if (!isDuplicate) {
      onAdd?.(period);
    }

    if (mode === 'block') setRange([]);
    else setSameDayRange({});
  };

  const handleRemove = (index: number) => {
    const newPeriods = [...periods];
    newPeriods.splice(index, 1);
    onChange?.(newPeriods);
    onRemove?.(newPeriods);
  };

  const isDisabled = () => {
    if (mode === 'block') return !(range?.[0] && range?.[1]);
    const { date, start, end } = sameDayRange || {};
    return !(date && start && end);
  };


  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      {/* Add Section */}
      <div className={styles.addLocationAvailability}>
        <div className={styles.inner}>
          {mode === 'open' ? (
            <SameDayTimeRangePicker
              notBeforeToday
              includeToday
              value={sameDayRange}
              onChange={setSameDayRange}
            />
          ) : (
            <MyDateTimeRangePicker
              styles={{ flex: 1, width: '100%', paddingRight: 15 }}
              onChange={setRange}
              ranges={range}
            />
          )}
          <Button
            className="button-secondary"
            disabled={isDisabled()}
            onClick={handleAdd}
            icon={<PlusOutlined/>}
            type="primary"
          >
          </Button>
        </div>
      </div>

      {/* List Section */}
      {periods?.length > 0 && (
        <div className={styles.blockPeriods}>
          <VirtualizedList
            data={periods}
            height={Math.min(periods.length * ITEM_HEIGHT, 350)}
            itemHeight={ITEM_HEIGHT}
            className={styles.list}
            itemKey={(item, index) => `${item.start}-${item.end}-${index}`}
          >
            {(period, index) => (
              <div
                className={styles.periodRow}
                key={`${period.start}-${period.end}-${index}`}
              >
                <div className={styles.inner}>
                  <div className={styles.periodInfo}>
                    {mode === 'block' ? (
                      <div className={styles.infoLine}>
                        <span className={styles.label}>
                          <F id="pages.locations.from" />
                        </span>
                        <DateFormat
                          ignoreLocalTimezone
                          format="YYYY-MM-DD HH:mm"
                          date={getPeriodDate(period.start, period.date)}
                        />
                        <span className={styles.separator}>→</span>
                        <span className={styles.label}>
                          <F id="pages.locations.to" />
                        </span>
                        <DateFormat
                          ignoreLocalTimezone
                          format="YYYY-MM-DD HH:mm"
                          date={getPeriodDate(period.end, period.date)}
                        />
                      </div>
                    ) : (
                      <div className={styles.infoLine}>
                        <DateFormat
                          ignoreLocalTimezone
                          format="YYYY-MM-DD"
                          date={getPeriodDate(period.start, period.date)}
                        />
                        <span className={styles.timeRange}>
                          <DateFormat
                            ignoreLocalTimezone
                            format="HH:mm"
                            date={getPeriodDate(period.start, period.date)}
                          />
                          {' → '}
                          <DateFormat
                            ignoreLocalTimezone
                            format="HH:mm"
                            date={getPeriodDate(period.end, period.date)}
                          />
                        </span>
                      </div>
                    )}
                  </div>
                  <div className={styles.action}>
                    <Popconfirm
                      // open={openIndex === index}
                      // onOpenChange={(open) => setOpenIndex(open ? index : null)}
                      onConfirm={() => {
                        setOpenIndex(null);
                        handleRemove(index);
                      }}
                      onCancel={() => setOpenIndex(null)}
                      placement="topRight"
                      destroyTooltipOnHide={true}
                      getPopupContainer={(trigger) =>
                        trigger.closest('.ant-drawer-content') ||
                        rootRef.current ||
                        document.body
                      }
                      title={
                        <F
                          entity={
                            mode === 'block'
                              ? INTL_ENTITY.BLOCK_PERIOD
                              : INTL_ENTITY.ADDITIONAL_OPEN_HOURS
                          }
                          action={INTL_ACTION.REMOVE_CONFIRMATION}
                          defaultMessage="Are you sure to remove this {entity}?"
                        />
                      }
                    >
                      <Button
                        type="text"
                        size="small"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: 0,
                          height: 24,
                          width: 24,
                        }}
                        icon={
                          <CloseCircleOutlined
                            style={{ color: '#787878', fontSize: 18 }}
                          />
                        }
                      />
                    </Popconfirm>
                  </div>
                </div>
              </div>
            )}
          </VirtualizedList>
        </div>
      )}
    </div>
  );
};
